import { lazy, useCallback, useEffect, useMemo, useState } from 'react'
import {
  Activity, Bot, CalendarRange, Columns3, GitBranch, List, PenTool, Plus, Search, Settings, Timer, X,
} from 'lucide-react'
import type { Project, Sprint, Taak, TaakSoort } from '../../gedeeld/types'
import { api } from '../lib/api'
import { PROJECT_STATUS, SOORTEN } from '../lib/opmaak'
import { navigeer, useTitel } from '../lib/router'
import { laadProjecten, nieuweTaak, projectBijwerken, useBasis, useStaat, useTaakWijzigingen, verwerk } from '../lib/staat'
import { Avatar, Badge, Foutvlak, Kies, Knop, Laden, Tabs } from './ui'
import Kanban from './project/Kanban'
import Lijst from './project/Lijst'
import Backlog from './project/Backlog'
import Tijdlijn from './project/Tijdlijn'
import Instellingen from './project/Instellingen'
import ProjectActiviteit from './project/ProjectActiviteit'

const Whiteboards = lazy(() => import('./whiteboard/Whiteboards'))
const ProjectCode = lazy(() => import('./code/ProjectCode'))
const ProjectAi = lazy(() => import('./ai/ProjectAi'))

/* ===========================================================================
 *  Eén project
 *
 *  De taken worden hier één keer opgehaald en gedeeld door het bord, de
 *  lijst, de backlog en de tijdlijn. Wisselen tussen die vier is dus
 *  meteen, en wat je op het bord verschuift staat in de lijst op dezelfde
 *  plek. Het filter bovenaan geldt voor alle vier.
 * =========================================================================== */

export type Tab = 'bord' | 'lijst' | 'backlog' | 'tijdlijn' | 'whiteboards' | 'code' | 'ai' | 'activiteit' | 'instellingen'

const TAB_NAAM: Record<Tab, string> = {
  bord: 'Bord', lijst: 'Lijst', backlog: 'Backlog en sprints', tijdlijn: 'Tijdlijn', whiteboards: 'Whiteboards',
  code: 'Code', ai: 'AI', activiteit: 'Activiteit', instellingen: 'Instellingen',
}

export interface Filter {
  zoek: string
  wie: string            // '' = iedereen, '-' = niemand
  soort: TaakSoort | ''
  label: string
  alleenSprint: boolean
}

const LEEG_FILTER: Filter = { zoek: '', wie: '', soort: '', label: '', alleenSprint: false }

export interface ProjectContext {
  project: Project
  taken: Taak[]
  zicht: Taak[]
  sprints: Sprint[]
  actieveSprint: Sprint | null
  setTaken: (f: (t: Taak[]) => Taak[]) => void
  herlaad: () => void
  setSprints: (s: Sprint[]) => void
}

export default function ProjectScherm({ id, tab }: { id: string; tab: string }) {
  const { projecten, mensen } = useBasis()
  const ikId = useStaat((s) => s.ik?.id)
  const [project, setProject] = useState<Project | null>(null)
  const [sprints, setSprints] = useState<Sprint[]>([])
  const [leden, setLeden] = useState<{ id: string; rol: string }[]>([])
  const [taken, setTaken] = useState<Taak[] | null>(null)
  const [fout, setFout] = useState<string | null>(null)
  const [filter, setFilter] = useState<Filter>(LEEG_FILTER)

  const laad = useCallback(async () => {
    setFout(null)
    try {
      const [p, t] = await Promise.all([api.project(id), api.taken({ projectId: id })])
      setProject(p.project)
      setSprints(p.sprints)
      setLeden(p.leden)
      setTaken(t)
    } catch (e) {
      setFout(e instanceof Error ? e.message : String(e))
    }
  }, [id])

  useEffect(() => { setTaken(null); setProject(null); void laad() }, [laad])

  /* Elders veranderd (de lade, het nieuwe-taakvenster): hier bijwerken. */
  useTaakWijzigingen(useCallback((t) => {
    setTaken((x) => (x ? verwerk(x, t, (y) => y.projectId === id) : x))
  }, [id]))

  /* Wat er in de winkel staat is sneller dan wat we ophalen; toon dat
     alvast als kop, zodat het scherm niet leeg begint. */
  const kop = project ?? projecten.find((p) => p.id === id) ?? null
  useTitel(kop?.naam, TAB_NAAM[tab as Tab] ?? null)

  const actieveSprint = sprints.find((s) => s.status === 'actief') ?? null

  const zicht = useMemo(() => {
    const z = filter.zoek.trim().toLowerCase()
    return (taken ?? []).filter((t) =>
      (!z || `${t.titel} ${t.omschrijving} ${kop?.sleutel ?? ''}-${t.nummer}`.toLowerCase().includes(z))
      && (!filter.wie || (filter.wie === '-' ? !t.toegewezenAan : t.toegewezenAan === filter.wie))
      && (!filter.soort || t.soort === filter.soort)
      && (!filter.label || t.labels.includes(filter.label))
      && (!filter.alleenSprint || (actieveSprint && t.sprintId === actieveSprint.id)))
  }, [taken, filter, kop?.sleutel, actieveSprint])

  if (fout) return <div className="pagina"><Foutvlak fout={fout} opnieuw={() => void laad()} /></div>
  if (!kop) return <Laden tekst="Het project wordt opgehaald…" />

  const t = tab as Tab
  const ctx: ProjectContext | null = project && taken ? {
    project, taken, zicht, sprints, actieveSprint,
    setTaken: (f) => setTaken((x) => f(x ?? [])),
    herlaad: () => void laad(),
    setSprints,
  } : null

  const mensenInProject = [...new Set((taken ?? []).map((x) => x.toegewezenAan).filter(Boolean) as string[])]
  const metFilter = ['bord', 'lijst', 'tijdlijn'].includes(t)
  const filterAan = JSON.stringify(filter) !== JSON.stringify(LEEG_FILTER)

  return (
    <div className="pagina project-pagina">
      <header className="project-kop">
        <div className="project-kop-titel">
          <span className={`project-sleutel groot t-${kop.kleur}`}>{kop.sleutel ?? '—'}</span>
          <div>
            <h1>{kop.naam}</h1>
            <span className="project-kop-meta">
              <Badge kleur={kop.status === 'actief' ? 'ok' : kop.status === 'gepauzeerd' ? 'warn' : 'info'}>{PROJECT_STATUS[kop.status]}</Badge>
              {actieveSprint && (
                <button type="button" className="link-knop" onClick={() => navigeer(`/p/${id}/backlog`)}>
                  <Timer size={13} /> {actieveSprint.naam}
                </button>
              )}
              {kop.githubRepo && (
                <button type="button" className="link-knop" onClick={() => navigeer(`/p/${id}/code`)}>
                  <GitBranch size={13} /> {kop.githubRepo.split('/')[1]}
                </button>
              )}
              {kop.archief && <Badge kleur="warn">In het archief</Badge>}
            </span>
          </div>
        </div>
        <div className="project-team">
          {leden.slice(0, 8).map((l) => {
            const m = mensen.find((x) => x.id === l.id)
            return <Avatar key={l.id} naam={m?.naam ?? '?'} id={l.id} grootte={26} />
          })}
          {leden.length > 8 && <span className="stil">+{leden.length - 8}</span>}
        </div>
        <Knop soort="hoofd" icoon={<Plus size={16} />} onClick={() => nieuweTaak({ projectId: id, sprintId: t === 'backlog' ? null : undefined })}>Taak</Knop>
      </header>

      <Tabs<Tab> waarde={t} onKies={(n) => navigeer(`/p/${id}/${n}`, true)} tabs={[
        { key: 'bord', label: 'Bord', icoon: <Columns3 size={14} /> },
        { key: 'lijst', label: 'Lijst', icoon: <List size={14} /> },
        { key: 'backlog', label: 'Backlog', icoon: <Timer size={14} /> },
        { key: 'tijdlijn', label: 'Tijdlijn', icoon: <CalendarRange size={14} /> },
        { key: 'whiteboards', label: 'Whiteboards', icoon: <PenTool size={14} /> },
        { key: 'code', label: 'Code', icoon: <GitBranch size={14} /> },
        { key: 'ai', label: 'AI', icoon: <Bot size={14} /> },
        { key: 'activiteit', label: 'Activiteit', icoon: <Activity size={14} /> },
        { key: 'instellingen', label: 'Instellingen', icoon: <Settings size={14} /> },
      ]} />

      {metFilter && ctx && (
        <div className="werkbalk filterbalk">
          <div className="zoekveld">
            <Search size={14} />
            <input value={filter.zoek} onChange={(e) => setFilter({ ...filter, zoek: e.target.value })} placeholder="Filter op tekst of nummer…" />
          </div>
          <div className="wie-filter">
            {mensenInProject.slice(0, 10).map((w) => {
              const m = mensen.find((x) => x.id === w)
              return (
                <button key={w} type="button" className={`wie-bol ${filter.wie === w ? 'actief' : ''}`}
                  title={m?.naam} onClick={() => setFilter({ ...filter, wie: filter.wie === w ? '' : w })}>
                  <Avatar naam={m?.naam ?? '?'} id={w} grootte={24} />
                </button>
              )
            })}
            {ikId && mensenInProject.includes(ikId) && (
              <button type="button" className={`filter-pil ${filter.wie === ikId ? 'actief' : ''}`}
                onClick={() => setFilter({ ...filter, wie: filter.wie === ikId ? '' : ikId })}>Alleen ik</button>
            )}
          </div>
          <Kies<TaakSoort | ''> klein waarde={filter.soort} onKies={(soort) => setFilter({ ...filter, soort })}
            opties={[{ key: '', label: 'Alle soorten' }, ...SOORTEN.map((s) => ({ key: s.key, label: s.label, kleur: s.kleur }))]} />
          {!!kop.labels.length && (
            <Kies<string> klein waarde={filter.label} onKies={(label) => setFilter({ ...filter, label })}
              opties={[{ key: '', label: 'Alle labels' }, ...kop.labels.map((l) => ({ key: l.naam, label: l.naam, kleur: l.kleur }))]} />
          )}
          {actieveSprint && (
            <label className="vinkje">
              <input type="checkbox" checked={filter.alleenSprint} onChange={(e) => setFilter({ ...filter, alleenSprint: e.target.checked })} />
              Alleen {actieveSprint.naam}
            </label>
          )}
          {filterAan && <Knop soort="stil" klein icoon={<X size={14} />} onClick={() => setFilter(LEEG_FILTER)}>Wissen</Knop>}
          <span className="vul" />
          <span className="stil">{zicht.length} van {taken?.length ?? 0}</span>
        </div>
      )}

      <div className="project-inhoud">
        {!ctx ? <Laden /> : (
          <>
            {t === 'bord' && <Kanban ctx={ctx} />}
            {t === 'lijst' && <Lijst ctx={ctx} />}
            {t === 'backlog' && <Backlog ctx={ctx} />}
            {t === 'tijdlijn' && <Tijdlijn ctx={ctx} />}
            {t === 'whiteboards' && <Whiteboards projectId={id} ingebed />}
            {t === 'code' && <ProjectCode project={ctx.project} />}
            {t === 'ai' && <ProjectAi project={ctx.project} taken={ctx.taken} />}
            {t === 'activiteit' && <ProjectActiviteit projectId={id} />}
            {t === 'instellingen' && (
              <Instellingen project={ctx.project} leden={leden}
                opOpgeslagen={(p) => { setProject(p); projectBijwerken(p) }}
                opLeden={(l) => { setLeden(l); void laadProjecten() }} />
            )}
          </>
        )}
      </div>
    </div>
  )
}
